// src/components/admin/flows/AutomationRulesList.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Zap, Plus, ArrowRight, Trash2, ToggleLeft, ToggleRight
} from 'lucide-react';
import { useFlowAutomation, generateId } from '../../../contexts/FlowAutomationContext';
import flowAutomationService from '../../../services/flowAutomation.service';
import { useTheme } from '../../../contexts/ThemeContext';
import { TRIGGER_EVENTS, ACTION_TYPES } from './canvas/nodeRegistry';
import EmptyState from './components/EmptyState';

const AutomationRulesList = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const isDark = theme === 'dark';
    const { saveWorkflow, initNewWorkflow } = useFlowAutomation();

    const [rules, setRules] = useState([]);

    // Load rules from backend on mount
    useEffect(() => {
        const loadRules = async () => {
            try {
                const result = await flowAutomationService.getWorkflows({ type: 'task_automation', scope: 'global' });
                if (result.workflows?.length) {
                    const loaded = result.workflows.map(wf => {
                        const triggerStep = wf.steps?.find(s => s.type === 'trigger');
                        const actionStep = wf.steps?.find(s => s.type === 'action');
                        return {
                            id: wf.id,
                            name: wf.name,
                            trigger: triggerStep?.config?.event || 'manual',
                            action: actionStep?.config?.action_type || 'send_notification',
                            params: actionStep?.config?.params || {},
                            enabled: wf.status === 'active',
                        };
                    });
                    setRules(loaded);
                }
            } catch (err) {
                console.error('Error loading automation rules:', err);
            }
        };
        loadRules();
    }, []);
    const [showNewRule, setShowNewRule] = useState(false);
    const [newRule, setNewRule] = useState({
        name: '',
        trigger: 'employee_joined',
        action: 'send_notification',
        params: { title: '', message: '' },
        enabled: true,
    });
    const [saving, setSaving] = useState(false);

    const inputClass = `w-full px-3 py-2 rounded-lg text-sm border ${
        isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
    } focus:ring-2 focus:ring-blue-500 focus:outline-none`;

    const labelClass = `block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`;

    const handleSaveRule = async () => {
        if (!newRule.name.trim()) return;
        setSaving(true);

        // Convert simple rule to a workflow
        const workflow = initNewWorkflow({
            name: newRule.name,
            type: 'task_automation',
            steps: [
                {
                    id: generateId('step'),
                    type: 'trigger',
                    label: TRIGGER_EVENTS.find(t => t.value === newRule.trigger)?.label || 'Trigger',
                    config: { event: newRule.trigger, filters: {} },
                    position: { x: 300, y: 80 },
                },
                {
                    id: generateId('step'),
                    type: 'action',
                    label: ACTION_TYPES.find(a => a.value === newRule.action)?.label || 'Action',
                    config: { action_type: newRule.action, params: newRule.params },
                    position: { x: 300, y: 230 },
                },
                {
                    id: generateId('step'),
                    type: 'end',
                    label: 'End',
                    config: {},
                    position: { x: 300, y: 380 },
                },
            ],
        });

        // Auto-connect
        workflow.connections = [
            { id: generateId('conn'), from_step_id: workflow.steps[0].id, to_step_id: workflow.steps[1].id },
            { id: generateId('conn'), from_step_id: workflow.steps[1].id, to_step_id: workflow.steps[2].id },
        ];

        await saveWorkflow(workflow);
        setSaving(false);

        const updated = [...rules, { ...newRule, id: workflow.id }];
        setRules(updated);
        saveRulesToStorage(updated);
        setNewRule({ name: '', trigger: 'employee_joined', action: 'send_notification', params: { title: '', message: '' }, enabled: true });
        setShowNewRule(false);
    };

    const saveRulesToStorage = (data) => {
        try { localStorage.setItem('hrportal_admin_automation_rules', JSON.stringify(data)); } catch {}
    };

    const triggerLabel = (val) => TRIGGER_EVENTS.find(t => t.value === val)?.label || val;
    const actionLabel = (val) => ACTION_TYPES.find(a => a.value === val)?.label || val;

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Quick Automations
                    </h3>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Simple if-then rules — no workflow builder needed
                    </p>
                </div>
                <button
                    onClick={() => setShowNewRule(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600"
                >
                    <Plus className="w-4 h-4" />
                    New Rule
                </button>
            </div>

            {/* New Rule Form */}
            {showNewRule && (
                <div className={`rounded-xl p-5 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
                    <h4 className={`text-sm font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Create Automation Rule
                    </h4>

                    <div className="space-y-3">
                        <div>
                            <label className={labelClass}>Rule Name</label>
                            <input
                                type="text"
                                value={newRule.name}
                                onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                                className={inputClass}
                                placeholder="e.g., Welcome notification for new hires"
                            />
                        </div>

                        <div className="flex items-center gap-3">
                            <div className="flex-1">
                                <label className={labelClass}>When this happens...</label>
                                <select
                                    value={newRule.trigger}
                                    onChange={(e) => setNewRule({ ...newRule, trigger: e.target.value })}
                                    className={inputClass}
                                >
                                    {TRIGGER_EVENTS.map(t => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </select>
                            </div>
                            <ArrowRight className={`w-5 h-5 mt-5 flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                            <div className="flex-1">
                                <label className={labelClass}>Do this...</label>
                                <select
                                    value={newRule.action}
                                    onChange={(e) => setNewRule({ ...newRule, action: e.target.value })}
                                    className={inputClass}
                                >
                                    {ACTION_TYPES.map(a => (
                                        <option key={a.value} value={a.value}>{a.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Action Params */}
                        {(newRule.action === 'send_notification' || newRule.action === 'send_email') && (
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelClass}>Title / Subject</label>
                                    <input
                                        type="text"
                                        value={newRule.params.title || ''}
                                        onChange={(e) => setNewRule({ ...newRule, params: { ...newRule.params, title: e.target.value } })}
                                        className={inputClass}
                                        placeholder="Notification title..."
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Message</label>
                                    <input
                                        type="text"
                                        value={newRule.params.message || ''}
                                        onChange={(e) => setNewRule({ ...newRule, params: { ...newRule.params, message: e.target.value } })}
                                        className={inputClass}
                                        placeholder="Use {{employee.name}} for variables"
                                    />
                                </div>
                            </div>
                        )}

                        {newRule.action === 'create_task' && (
                            <div>
                                <label className={labelClass}>Task Title</label>
                                <input
                                    type="text"
                                    value={newRule.params.title || ''}
                                    onChange={(e) => setNewRule({ ...newRule, params: { ...newRule.params, title: e.target.value } })}
                                    className={inputClass}
                                    placeholder="e.g., Complete onboarding checklist"
                                />
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-2 mt-4">
                        <button
                            onClick={() => setShowNewRule(false)}
                            className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveRule}
                            disabled={saving || !newRule.name.trim()}
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50"
                        >
                            {saving ? 'Creating...' : 'Create Rule'}
                        </button>
                    </div>
                </div>
            )}

            {/* Rules List */}
            {rules.length === 0 && !showNewRule ? (
                <EmptyState
                    icon={Zap}
                    title="No automation rules yet"
                    description="Create simple if-then rules to automate repetitive HR tasks without building a full workflow."
                    actionLabel="Create First Rule"
                    onAction={() => setShowNewRule(true)}
                />
            ) : (
                <div className="space-y-2">
                    {rules.map((rule) => (
                        <div
                            key={rule.id}
                            className={`flex items-center justify-between p-4 rounded-xl border ${
                                isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                            }`}
                        >
                            <div className="flex items-center gap-4">
                                <Zap className={`w-5 h-5 ${rule.enabled ? 'text-amber-500' : isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                                <div>
                                    <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{rule.name}</p>
                                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        When {triggerLabel(rule.trigger)} → {actionLabel(rule.action)}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => navigate(`/admin/flows/builder/${rule.id}`)}
                                    className={`text-xs px-2 py-1 rounded ${isDark ? 'text-blue-400 hover:bg-gray-700' : 'text-blue-500 hover:bg-gray-100'}`}
                                >
                                    Edit
                                </button>
                                <button className={`p-1 rounded ${isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-400 hover:bg-gray-100'}`}>
                                    {rule.enabled ? (
                                        <ToggleRight className="w-5 h-5 text-green-500" />
                                    ) : (
                                        <ToggleLeft className="w-5 h-5" />
                                    )}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AutomationRulesList;
